import { useState, useEffect, useMemo } from 'react';
import React from 'react';
import styles from './SkillPaths.module.scss';
import { format, parseISO } from 'date-fns';
import folderIcon from 'assets/icons/folder_icon.svg';
import miniFolder from 'assets/icons/mini_folder.svg';
import checkIcon from 'assets/icons/check.svg';
import { Schedule, Event, Person } from '@material-ui/icons';
import { useGetSkillSetUserDetails } from 'features/training/Training.queries';
import InputBar from './InputBar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Avatar,
  CircularProgress,
} from '@material-ui/core';
import ModulesRequiredIcon from 'features/users/components/Settings/ModulesRequiredIcon';

const SkillPathDetailModalBody = ({ skillPath = {}, skillPathDetail = {} }) => {
  const [skillSetSelected, setSillSetSelected] = useState('');
  const [search, setSearch] = useState('');

  const { data: usersDetails = {}, isLoading } = useGetSkillSetUserDetails({
    uuid: skillPath?.uuid,
    skill_path_uuid: skillPath?.uuid,
    skill_set_uuid: skillSetSelected,
  });

  const onSkillSetChange = (uuid) => setSillSetSelected(uuid);
  const handleInputChange = (e) => setSearch(e.target.value);

  useEffect(() => {
    if (!skillPathDetail?.data?.contents?.length > 0) return;
    setSillSetSelected(skillPathDetail?.data?.contents[0]?.uuid);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skillPathDetail?.data]);

  const getSkillSetName = useMemo(() => {
    const contents = skillPathDetail?.data?.contents;
    return contents?.find((i) => i?.uuid === skillSetSelected)?.title ?? '';
  }, [skillPathDetail?.data?.contents, skillSetSelected]);

  const getSkillSetTime = useMemo(() => {
    const contents = skillPathDetail?.data?.contents;
    return contents?.find((i) => i?.uuid === skillSetSelected)?.estimated_duration ?? '--';
  }, [skillPathDetail?.data?.contents, skillSetSelected]);

  const filteredUsers = useMemo(() => {
    if (!Array.isArray(usersDetails)) return [];

    if (search === '') return usersDetails;
    return usersDetails?.filter((item) => item?.name.toLowerCase().includes(search.toLowerCase()));
  }, [search, usersDetails]);

  const statusLabel = (key) =>
    key === 'completed' ? 'Completed' : key === 'in-progress' ? 'In progress' : 'Not yet started';

  const getUserRole = (role) => {
    if (role === 'Admin') return 2;
    if (role === 'Sales Management') return 3;
    if (role === 'Sales Rep') return 4;
    return 1;
  };

  return (
    <>
      <div className={styles.leftContent}>
        <div className={styles.skillSetList}>
          <p className={styles.title}>Skill Sets</p>
          <div>
            {skillPathDetail?.data?.contents?.map((content) => (
              <div
                key={content?.uuid}
                className={styles.item}
                style={
                  content?.uuid === skillSetSelected
                    ? { borderLeft: '4px solid rgba(4, 148, 253, 1)' }
                    : null
                }
                onClick={() => onSkillSetChange(content?.uuid)}
              >
                <div className={styles.icon}>
                  {content?.status?.key === 'completed' ? (
                    <img
                      alt="icon_skill"
                      src={checkIcon}
                      style={{ width: '34px', height: '34px' }}
                    />
                  ) : (
                    <img
                      alt="icon_skill"
                      src={folderIcon}
                      style={{ width: '34px', height: '34px' }}
                    />
                  )}
                </div>
                <div className={styles.detail}>
                  <p className={styles.text}>{content?.title}</p>
                  <div className={styles.subDetail}>
                    <div>
                      <img alt="icon_skill" src={miniFolder} />
                      <span>{content?.content_count} Contents</span>
                    </div>
                    <div>
                      <Schedule style={{ fontSize: '1rem', color: 'rgba(144, 164, 174, 1)' }} />
                      <span>{content?.estimated_duration} Min</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.rightContent}>
        {isLoading ? (
          <div className={styles.loader}>
            <CircularProgress />
          </div>
        ) : (
          <div className={styles.skillSetUsers}>
            <div className={styles.header}>
              <div className={styles.title}>
                <p>{getSkillSetName}</p>
              </div>
            </div>
            <div className={styles.details}>
              <div>
                <Person style={{ fontSize: '1rem', color: 'rgba(144, 164, 174, 1)' }} />
                <span>{skillPath?.author}</span>
              </div>
              <div>
                <div className={styles.remark}>
                  <Event style={{ fontSize: '1rem', color: 'rgba(144, 164, 174, 1)' }} />
                  <span>
                    Published on
                    {skillPath?.published_at
                      ? ` ${format(parseISO(skillPath?.published_at), 'MMMM do, yyyy')}`
                      : '--'}
                  </span>
                </div>
                <div className={styles.remark}>
                  <Schedule style={{ fontSize: '1rem', color: 'rgba(144, 164, 174, 1)' }} />
                  <span>Recommended time is {getSkillSetTime} min</span>
                </div>
              </div>
            </div>

            <InputBar value={search} handleChange={handleInputChange} />

            <div className={styles.table}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell align="left" style={{ width: 100 }}>
                      <p>name</p>
                    </TableCell>
                    <TableCell align="left" style={{ width: 60 }}>
                      <p> time spent</p>
                    </TableCell>
                    <TableCell align="left" style={{ width: 70 }}>
                      <p>completion</p>
                    </TableCell>
                    <TableCell align="left" style={{ width: 70 }}>
                      <p>assessment</p>
                    </TableCell>
                    <TableCell align="left" style={{ width: 60 }}>
                      <p>role</p>
                    </TableCell>
                    <TableCell align="left" style={{ width: 60 }}>
                      <p>status</p>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers?.length > 0 ? (
                    filteredUsers?.map((item, index) => (
                      <TableRow key={item.id}>
                        <TableCell align="left" style={{ width: 100 }}>
                          <div className={styles.userContainer}>
                            <Avatar alt="" src={item?.avatar}>
                              {item?.name[0]}
                            </Avatar>
                            <p>{item?.name}</p>
                          </div>
                        </TableCell>
                        <TableCell align="left" style={{ width: 60 }}>
                          <span>{item?.time_spent} MIN.</span>
                        </TableCell>
                        <TableCell align="left" style={{ width: 70 }}>
                          {' '}
                          <span>{item?.overall_completion}%</span>
                        </TableCell>
                        <TableCell align="left" style={{ width: 70 }}>
                          <span>
                            {item?.module_assessment_rating_percentages
                              ? `${item?.module_assessment_rating_percentages}%`
                              : `--`}
                          </span>
                        </TableCell>
                        <TableCell align="left" style={{ width: 60 }}>
                          <p
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'baseline',
                            }}
                          >
                            <span>{item?.role}</span>
                            <ModulesRequiredIcon
                              user={{
                                is_onboarding_enabled: item?.is_onboarding_enabled,
                                is_training_enabled: item?.is_training_enabled,
                                user_role: getUserRole(item?.role),
                              }}
                            />
                          </p>
                        </TableCell>
                        <TableCell align="left" style={{ width: 60 }}>
                          <h6
                            className={
                              item?.status?.key === 'completed'
                                ? styles.completed_skill
                                : item?.status?.key === 'in-progress'
                                  ? styles.in_progress_skill
                                  : styles.not_started_skill
                            }
                          >
                            {statusLabel(item?.status?.key)}
                          </h6>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <p style={{ color: '#78909c' }}>No matching results were found</p>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SkillPathDetailModalBody;
